import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useMatch } from "react-router-dom";
import {
  MARKETING_ICON,
  MARKETING_ICON_FILLED,
} from "../../../../redo-suite/marketing-cloud";

export type EmailSmsSubRoute =
  | "welcome"
  | "ai-personas"
  | "audience"
  | "deliverability"
  | "campaigns"
  | "segments"
  | "templates/email"
  | "templates/sms"
  | "popups"
  | "spotlights";

export const EmailSMSItems = memo(function MarketingItems() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const settings = team.settings;

  const emailSmsRoot = routes.emailSmsHref;

  const marketingEnabled = settings.marketing?.enabled;

  const showCampaigns =
    marketingEnabled && settings?.marketing?.campaigns?.enabled;

  const showAiPersonas = marketingEnabled && settings?.marketing?.ai?.enabled;

  // TODO: enable when feature is ready
  const showDeliverabilityReport = false;

  const anyChildSelected = useMatch(`${emailSmsRoot}/*`);

  const primaryHref = marketingEnabled
    ? emailSmsRoot
    : routes.getEmailSmsSubRoute("welcome");

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: primaryHref }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={MARKETING_ICON_FILLED}
      friendlyName={t("Email & SMS", {
        context: "merchant marketing navigation label",
      })}
      id="email-sms"
      UnfilledIcon={MARKETING_ICON}
    >
      <EmailSmsAiPersonasItem hidden={!showAiPersonas} />
      <EmailSmsAudienceItem />
      <EmailSmsCampaignsItem hidden={!showCampaigns} />
      <EmailSmsDeliverabilityItem hidden={!showDeliverabilityReport} />
      <EmailSmsEmailLibraryItem />
      <EmailSmsSegmentsItem hidden={!showCampaigns} />
      <EmailSmsSmsTemplatesItem />
      <EmailSmsPopupsItem hidden={!marketingEnabled} />
      <EmailSmsWelcomeItem hidden={marketingEnabled} />
    </RedoSecondaryNavItem>
  );
});

const EmailSmsAiPersonasItem = memo(function EmailSmsAiPersonasItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("ai-personas") }}
      friendlyName={t("AI personas", {
        context: "merchant marketing navigation label",
      })}
      hidden={hidden}
      id="ai-personas"
    />
  );
});

const EmailSmsAudienceItem = memo(function EmailSmsAudienceItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("audience") }}
      friendlyName={t("Audience", {
        context:
          "marketing navigation label for message recipients and customer audiences",
      })}
      id="audience"
    />
  );
});

const EmailSmsCampaignsItem = memo(function EmailSmsCampaignsItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("campaigns") }}
      friendlyName={t("Campaigns", {
        context: "merchant marketing navigation label",
      })}
      hidden={hidden}
      id="campaigns"
    />
  );
});

const EmailSmsDeliverabilityItem = memo(function EmailSmsDeliverabilityItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getEmailSmsSubRoute("deliverability"),
      }}
      friendlyName={t("Deliverability", {
        context:
          "marketing navigation label for email and SMS delivery performance",
      })}
      hidden={hidden}
      id="deliverability"
    />
  );
});

const EmailSmsEmailLibraryItem = memo(function EmailSmsEmailLibraryItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getEmailSmsSubRoute("templates/email"),
      }}
      friendlyName={t("Email library", {
        context: "merchant marketing navigation label",
      })}
      id="email-templates"
    />
  );
});

const EmailSmsSegmentsItem = memo(function EmailSmsSegmentsItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("segments") }}
      friendlyName={t("Segments", {
        context: "marketing navigation label for customer audience segments",
      })}
      hidden={hidden}
      id="segments"
    />
  );
});

const EmailSmsSmsTemplatesItem = memo(function EmailSmsSmsTemplatesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getEmailSmsSubRoute("templates/sms"),
      }}
      friendlyName={t("SMS templates", {
        context: "merchant marketing navigation label",
      })}
      id="sms-templates"
    />
  );
});

const EmailSmsPopupsItem = memo(function EmailSmsPopupsItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("popups") }}
      friendlyName={t("Popups", {
        context: "merchant marketing navigation label",
      })}
      hidden={hidden}
      id="popups"
    />
  );
});

const EmailSmsWelcomeItem = memo(function EmailSmsWelcomeItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("welcome") }}
      friendlyName={t("Welcome", {
        context: "email and SMS navigation page for welcome messaging",
      })}
      hidden={hidden}
      id="welcome"
    />
  );
});
